import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Input, Row, Select, Typography } from "antd";
import { ArrowLeftOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useGetNPSResponsesQuery } from "../../../apis/nps";
import { getResponseColumns, getResponseSkeletonColumns, getSkeletonRows } from "./npsTableColumns";
// import { downloadResponsesAsCsv } from "../utils/npsUtils";
import CustomTableWithPagination from "../../../components/CustomTableWithPagination/CustomTableWithPagination";
import NoDataFound from "../../../components/EmptyTable/NoDataFound";
import NPSChevronIcon from "./NPSChevronIcon";
import "./NPSAllResponsesView.css";

const { Text, Title } = Typography;

const NPSAllResponsesView = ({ total, presetScore, dateRangeDays, city: initialCity, cityOptions, onBack }) => {
  const [search, setSearch] = useState("");
  const [segment, setSegment] = useState("");
  const [score, setScore] = useState(presetScore != null ? String(presetScore) : "");
  const [city, setCity] = useState(initialCity || "");
  const [page, setPage] = useState(1);
  const scoreBadges = useRef(new Map());

  const registerScoreRef = (id, el) => {
    if (el) scoreBadges.current.set(id, el);
    else scoreBadges.current.delete(id);
  };

  useEffect(() => {
    if (presetScore != null) {
      setScore(String(presetScore));
      setSegment("");
      setPage(1);
    }
  }, [presetScore]);

  const params = useMemo(() => {
    const p = {
      start_date: dayjs().subtract(dateRangeDays, "day").format("YYYY-MM-DD"),
      end_date: dayjs().format("YYYY-MM-DD"),
      page,
    };
    if (search) p.search = search;
    if (segment) p.segment = segment;
    if (score !== "") p.score = score;
    if (city) p.city = city;
    return p;
  }, [dateRangeDays, page, search, segment, score, city]);

  const { data, isFetching } = useGetNPSResponsesQuery(params);
  const responses = data?.data?.results || [];
  const pagination = data?.data?.pagination;
  const totalCount = pagination?.total_entries ?? total;
  const itemsPerPage = pagination
    ? pagination.end_index - pagination.start_index + 1
    : 20;

  const updateFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setSegment("");
    setScore("");
    setCity("");
    setPage(1);
  };

  const hasFilters = Boolean(search || segment || score !== "" || city);
  const CITY_OPTIONS = [{ value: "", label: "All cities" }, ...cityOptions];
  const SEGMENT_OPTIONS = [
    { value: "", label: "All segments" },
    ...(data?.data?.filters?.segments || []).map((s) => ({ value: s.value, label: s.label })),
  ];
  const SCORE_OPTIONS = [
    { value: "", label: "All scores" },
    ...(data?.data?.filters?.scores || []).map((s) => ({ value: String(s.value), label: `Score ${s.label}` })),
  ];
  const searchPlaceholder = data?.data?.filters?.search?.placeholder || "Search order ID or customer";

  return (
    <div className="nps-all-responses">
      <button type="button" className="nps-back-btn" onClick={onBack}>
        <ArrowLeftOutlined /> Back to NPS overview
      </button>

      <div className="nps-page-head">
        <div>
          <Title level={3} className="nps-page-title">All responses</Title>
          <Text className="nps-page-sub">
            {hasFilters
              ? `${totalCount.toLocaleString()} responses match your filters`
              : `${totalCount.toLocaleString()} survey responses · score only, no written feedback collected`}
          </Text>
        </div>
        {/* <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => downloadResponsesAsCsv(responses, "nps-responses.csv")}
        >
          Export CSV
        </Button> */}
      </div>

      <Card className="nps-card nps-all-responses-card">
        <Row gutter={[10, 10]} className="nps-filters-row">
          <Col xs={24} sm={12} lg={8}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "#8496A8" }} />}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => updateFilter(setSearch)(e.target.value)}
            />
          </Col>
          <Col xs={12} sm={6} lg={4}>
            <Select className="nps-filter-select" value={segment} onChange={updateFilter(setSegment)} options={SEGMENT_OPTIONS} suffixIcon={<NPSChevronIcon />} />
          </Col>
          <Col xs={12} sm={6} lg={4}>
            <Select className="nps-filter-select" value={score} onChange={updateFilter(setScore)} options={SCORE_OPTIONS} suffixIcon={<NPSChevronIcon />} />
          </Col>
          <Col xs={12} sm={6} lg={4}>
            <Select className="nps-filter-select" value={city} onChange={updateFilter(setCity)} options={CITY_OPTIONS} suffixIcon={<NPSChevronIcon />} />
          </Col>
          <Col xs={12} sm={6} lg={4} className="nps-filters-clear-col">
            <Button type="link" onClick={clearFilters} disabled={!hasFilters}>Clear filters</Button>
          </Col>
        </Row>

        <CustomTableWithPagination
          className="nps-responses-table"
          rowKey="id"
          tableDataSource={isFetching ? getSkeletonRows(itemsPerPage) : responses}
          tableColumns={isFetching ? getResponseSkeletonColumns() : getResponseColumns(registerScoreRef)}
          scroll={{ x: 640 }}
          isLoading={false}
          currentPage={page}
          handlePageChange={setPage}
          itemsPerPage={itemsPerPage}
          totalEntries={totalCount}
          onRow={isFetching ? undefined : (record) => ({
            onMouseEnter: () => scoreBadges.current.get(record.id)?.play(),
          })}
          locale={{
            emptyText: (
              <NoDataFound text={`No responses match these filters. Clear them to see all ${totalCount.toLocaleString()}.`} />
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default NPSAllResponsesView;
